import { type FunctionComponent, type ReactNode, useState } from 'react';

import {
  Button,
  Input,
  newAsyncDataEnvelope,
} from '@dr.pogodin/react-utils';

import Table from 'components/Table';
import { AdminPage } from 'components/admin';
import AddNewButton from 'components/admin/AddNewButton';

import useUserArtist from 'hooks/useUserArtist';
import { type QueryT, useVideos } from 'hooks/video';

import {
  P_ADMIN_ADD_NEW_VIDEO,
  type VideoSortT,
  type VideoT,
  getId,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import IconFilters from 'assets/images/icon-filters-24px.svg';

import IconSearch from 'assets/images/icon-search-24px.svg';

import secondaryButtonTheme from 'themes/Button/admin/secondary.scss';

import { FiltersPanel, type FiltersT, countFilters } from './FiltersPanel';
import VideoRow from './VideoRow';

// TODO: This input with search icon is also used on other pages,
// it should be split into a dedicated component.
import inputTheme from './input.scss';

import S from './style.scss';

// eslint-disable-next-line complexity
const AdminContentManagementPage: FunctionComponent = () => {
  const [user] = useGlobalState('user');
  if (!user) throw Error('Unauthorized');

  const [openFilters, setOpenFilters] = useState(false);

  if (
    !user.permissions.EDIT_ANY_VIDEO_DETAILS
    && !user.permissions.VIDEO_UPLOAD
  ) throw Error('Forbidden');

  const userArtist = useUserArtist();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [textSearch, setTextSearch] = useState('');
  const [textSearchInput, setTextSearchInput] = useState('');

  let disabled: boolean | undefined;
  let query: QueryT | undefined;

  if (!user.permissions.EDIT_ANY_VIDEO_DETAILS) {
    if (userArtist.data) {
      query = { artistIds: { [getId(userArtist.data)]: true } };
    } else disabled = true;
  }

  if (textSearch) {
    query ??= {};
    query.textSearch = textSearch;
  }

  const [filters, setFilters] = useState<FiltersT>({});
  const [sort, setSort] = useState<VideoSortT>();

  const numFilters = countFilters(filters);
  const filtersCountNode: ReactNode = numFilters ? (
    <div styleName="numFilters">
      {numFilters}
    </div>
  ) : null;

  if (numFilters) {
    query ??= {};
    query.customModalities = filters.customModalities;

    if (filters.intents) {
      query.intents = {};
      for (const intent of filters.intents) {
        query.intents[intent] = true;
      }
    }

    if (
      filters.maxDuration !== undefined
      || filters.minDuration !== undefined
    ) {
      query.durationMin = [
        filters.minDuration ?? 0,
        filters.maxDuration ?? Number.MAX_SAFE_INTEGER,
      ];
    }
    if (filters.modalities) {
      query.modalities = {};
      for (const mod of filters.modalities) {
        query.modalities[mod] = true;
      }
    }
    if (filters.practitioner) {
      query.artistIds = {};
      if (filters.practitioner.id) {
        query.artistIds[filters.practitioner.id] = true;
      }
    }
  }

  const videos = useVideos({
    disabled,
    page,
    pageSize,
    query,
    sort,
  });

  const gs = useGlobalStateObject();

  const rows = new Array<ReactNode>(videos.items.length);
  for (let i = 0; i < videos.items.length; ++i) {
    const video = videos.items[i];
    if (!video) throw Error('Missing video');
    rows[i] = (
      <VideoRow
        key={getId(video)}
        reloadVideos={videos.reload}
        video={video}
      />
    );
  }

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle="Upload videos, create and organize content"
      title="Content Management"
    >
      <div styleName="header">
        {/*
          * TODO: It is the same field as in "shared/pages/admin/Practitioners",
          * it should go into a dedicated component!
          */}
        <div styleName="searchWrapper">
          <Input
            onBlur={() => {
              setTextSearchInput(textSearch);
            }}
            onChange={(e) => {
              setTextSearchInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setTextSearch(textSearchInput);
              }
            }}
            placeholder="Search"
            theme={inputTheme}
            value={textSearchInput}
          >
            <IconSearch styleName="searchIcon" />
          </Input>
        </div>
        <div styleName="filler" />
        <div styleName="actions">
          <AddNewButton
            disabled={
              // NOTE: The way it is organized now, it is possible to remove
              // "ARTIST" role from the user, but to still have his Artist object
              // in the database, which will be retrieved as userArtist here.
              !user.permissions.VIDEO_UPLOAD || userArtist.data?.status !== 'Active'
            }
            hideLabelOnMobile
            label="Add New Video"
            onClick={() => {
              gs.set('editedVideo', newAsyncDataEnvelope<VideoT>());
            }}
            to={P_ADMIN_ADD_NEW_VIDEO}
          />
          <Button
            onClick={() => {
              setOpenFilters(!openFilters);
            }}
            theme={secondaryButtonTheme}
          >
            <div styleName="iconButtonContent black">
              <IconFilters /> <span styleName="filterLabel">Filter {filtersCountNode}</span>
            </div>
          </Button>
        </div>
      </div>
      {openFilters ? (
        <FiltersPanel
          isPractitioner={!user.permissions.EDIT_ANY_VIDEO_DETAILS}
          onChange={setFilters}
          onClose={() => {
            setOpenFilters(false);
          }}
          value={filters}
        />
      ) : null}
      <Table
        busy={videos.loading}
        numPages={videos.numPages ?? 1}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 50, 100]}
        setPage={setPage}
        setPageSize={setPageSize}
      >
        <thead>
          <tr>
            <th>Thumbnail</th>
            <th>
              <span
                onClick={() => {
                  setSort({ title: sort?.title === 1 ? -1 : 1 });
                }}
                role="presentation"
                styleName="sortHandle"
              >
                Title

                {sort?.title === 1 ? '▼' : null}
                {sort?.title === -1 ? '▲' : null}
              </span>
            </th>
            {user.permissions.EDIT_ANY_VIDEO_DETAILS
              ? (
                <th>
                  <span
                    onClick={() => {
                      setSort({ artistName: sort?.artistName === 1 ? -1 : 1 });
                    }}
                    role="presentation"
                    styleName="sortHandle"
                  >
                    Created By

                    {sort?.artistName === 1 ? '▼' : null}
                    {sort?.artistName === -1 ? '▲' : null}
                  </span>
                </th>
              ) : null}
            <th>
              <span
                onClick={() => {
                  setSort({ createdAt: sort?.createdAt === 1 ? -1 : 1 });
                }}
                role="presentation"
                styleName="sortHandle"
              >
                Created At

                {sort?.createdAt === 1 ? '▼' : null}
                {sort?.createdAt === -1 ? '▲' : null}
              </span>
            </th>
            <th>
              <span
                onClick={() => {
                  setSort({ modalities: sort?.modalities === 1 ? -1 : 1 });
                }}
                role="presentation"
                styleName="sortHandle"
              >
                Modalities

                {sort?.modalities === 1 ? '▼' : null}
                {sort?.modalities === -1 ? '▲' : null}
              </span>
            </th>
            <th>
              <span
                onClick={() => {
                  setSort({ duration: sort?.duration === 1 ? -1 : 1 });
                }}
                role="presentation"
                styleName="sortHandle"
              >
                Duration

                {sort?.duration === 1 ? '▼' : null}
                {sort?.duration === -1 ? '▲' : null}
              </span>
            </th>
            <th>
              <span
                onClick={() => {
                  setSort({ publishedAt: sort?.publishedAt === 1 ? -1 : 1 });
                }}
                role="presentation"
                styleName="sortHandle"
              >
                Published Date

                {sort?.publishedAt === 1 ? '▼' : null}
                {sort?.publishedAt === -1 ? '▲' : null}
              </span>
            </th>
            <th>
              <span
                onClick={() => {
                  setSort({ status: sort?.status === 1 ? -1 : 1 });
                }}
                role="presentation"
                styleName="sortHandle"
              >
                Status

                {sort?.status === 1 ? '▼' : null}
                {sort?.status === -1 ? '▲' : null}
              </span>
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
    </AdminPage>
  );
};

export default AdminContentManagementPage;
